import {
  InformationOutline18Icon,
  LinkOutOutline18Icon,
} from "@vygruppen/spor-icon-react";
import { ISirkModuleForShifts } from "features/CenterContent/RoleContent/ISirk/ISirkModuleForShifts";
import { ShiftSchedule } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/ShiftSchedule";
import { VehicleReplacement } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleReplacement/VehicleReplacement";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { Module } from "shared/components/Module";
import {
  Badge,
  Box,
  Button,
  HStack,
  Text,
  Tooltip,
} from "shared/components/Spor";
import { AlternativeTransportVehicle } from "shared/types/alternativeTransport";
import { LANDING } from "shared/utils/routes";
import { useDropsRole } from "stores/useDropsRole";
import { generatePath, useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";

const ShiftModule = styled(Module).attrs({ size: "lg" })`
  height: 100%;
  min-height: 0;

  && {
    border: 1px solid ${({ theme }) => theme.colorOutlineSecondary};
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

const HorizontalScroll = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: auto;
  overflow-y: hidden;
`;

const ScrollContent = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 809px;
  display: flex;
  flex-direction: column;

  @container altTransportPage (width < 1696px) {
    min-width: 0;
  }
`;

const ScrollModuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(400px, 812px));
  gap: 9px;
  align-items: start;
  padding-bottom: 50px;
  column-gap: 0;

  @container altTransportPage (width < 1696px) {
    grid-template-columns: 1fr;
  }
`;

const SkiftplanModule = styled(Module)`
  @container altTransportPage (width >= 1696px) {
    && {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
`;

const ISirkModule = styled(Module)`
  @container altTransportPage (width >= 1696px) {
    && {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
`;

const EmptyContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  border: 1px solid ${({ theme }) => theme.colorOutlineSecondary};
  border-radius: 6px;
  background-color: ${({ theme }) => theme.colorBackgroundMain};
`;

interface AlternativeTransportRightPanelProps {
  vehicle: AlternativeTransportVehicle;
}

export const AlternativeTransportRightPanel = ({
  vehicle,
}: AlternativeTransportRightPanelProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { role } = useDropsRole();

  if (!vehicle.workShiftPlan) {
    return (
      <EmptyContainer>
        <Text fontWeight="400" fontSize="md">
          Ikke knyttet til skift
        </Text>
      </EmptyContainer>
    );
  }

  const { workShiftPlan, operatorName } = vehicle;
  const { workShiftTrips, shiftId, shiftDate, isirkId } = workShiftPlan;

  const distributionStopReferences = [
    ...(vehicle.itinerary?.stops ?? []),
    ...(vehicle.itinerary?.returnStops ?? []),
  ].map((stop) => stop.stopReference);

  const timeInterval =
    workShiftTrips && workShiftTrips.length > 0
      ? `${workShiftTrips[0]?.scheduledDepartureTimeOrigin?.toLocaleTimeString(
          "no-NO",
          { hour: "2-digit", minute: "2-digit" },
        )}–${workShiftTrips[
          workShiftTrips.length - 1
        ]?.scheduledArrivalTimeDestination?.toLocaleTimeString("no-NO", {
          hour: "2-digit",
          minute: "2-digit",
        })}`
      : "";

  return (
    <ShiftModule
      title={`Skift: ${shiftId ?? ""}`}
      action1={
        <VehicleReplacement
          vehicle={vehicle}
          distributionStopReferences={distributionStopReferences}
        />
      }
    >
      <Box flexShrink="0" mb="9px">
        {operatorName && workShiftTrips ? (
          <>
            <Text color={theme.colorTextSecondary}>{operatorName}</Text>
            <HStack>
              <Text>{timeInterval}</Text>
              <Text>({shiftDate?.toLocaleDateString("no-NO")})</Text>
            </HStack>
          </>
        ) : (
          <NoDataMessage reason="Det finnes ikke skiftinfo denne bussen" />
        )}
      </Box>
      <HorizontalScroll>
        <ScrollContent>
          <Box flex="1" minH="0" overflowY="auto" overflowX="hidden">
            <ScrollModuleGrid>
              <SkiftplanModule title="Skiftplan">
                <ShiftSchedule vehicle={vehicle} />
              </SkiftplanModule>

              <ISirkModule
                title={
                  <HStack gap="6px">
                    <Text>I-sirklogg</Text>
                    <Tooltip
                      style={{
                        width: "220px",
                        color: theme.colorBackgroundMain,
                        backgroundColor: theme.colorSuccessTextSecondary,
                        padding: "6px",
                        borderRadius: "12px",
                      }}
                      label="Denne loggen sendes til i-sirkloggen. Her vises kun det som er lagt inn på dette skiftet. Kundeveiledere har ikke tilgang til denne loggen."
                    >
                      <InformationOutline18Icon />
                    </Tooltip>
                  </HStack>
                }
                badgeRight={
                  <Badge p={1} colorPalette="grey" fontWeight="600">
                    {isirkId || "ukjent"}
                  </Badge>
                }
                action1={
                  <Button
                    size="xs"
                    fontSize="xs"
                    variant="tertiary"
                    onClick={() =>
                      navigate(
                        generatePath(LANDING, {
                          roleIdParam: role,
                          tabIdParam: "start",
                        }),
                      )
                    }
                  >
                    I-sirklogg <LinkOutOutline18Icon />
                  </Button>
                }
              >
                <ISirkModuleForShifts
                  selectedDate={shiftDate?.toString() ?? ""}
                  shiftId={shiftId ?? ""}
                  operatorName={operatorName || ""}
                />
              </ISirkModule>
            </ScrollModuleGrid>
          </Box>
        </ScrollContent>
      </HorizontalScroll>
    </ShiftModule>
  );
};

export default AlternativeTransportRightPanel;
